import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useCopy } from '../../../../../shared/clipboard/clipboard'
import { IconCheck, IconCopy } from '../../../../../shared/icon/icon'
import '../api-keys.scss'

// A new key's secret (a create, or a rotate's new secret), shown once:
// content returns it only in that answer and keeps a hash. The person
// copies it and says they've stored it; Escape and the backdrop don't close
// the dialog, so it is never lost by a stray key or click.
//
// The secret lives only in this dialog's props, handed down by the page
// that holds it until onDone, when the page lets go of it: nothing else
// keeps it (no storage, no list row, no later render).
export const ApiKeySecretDialog = ({ keyName, secret, rotated, graceText, onDone }) => {
  const titleId = useId()
  const textRef = useRef(null)
  const doneRef = useRef(null)
  const copy = useCopy()

  useEffect(() => {
    if (doneRef.current) doneRef.current.focus({ preventScroll: true })
  }, [])

  const swallowEscape = e => {
    if (e.key === 'Escape') e.stopPropagation()
  }

  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" data-testid="api-key-secret-backdrop">
      <div className="sw-dialog ak-secret-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={swallowEscape}>
        <h2 className="h5" id={titleId}>Copy your new API key</h2>
        <p className="mb-2">
          {rotated ? <>The new secret for <strong>{keyName}</strong>.</> : <>The key <strong>{keyName}</strong> is ready.</>}
          {' '}This is the only time it is shown: EducationXR keeps only a hash of it, so it can't be shown again.
          {rotated && graceText ? ` ${graceText}` : ''}
        </p>
        <div className="ak-secret">
          <code className="ak-secret-value sw-mono" data-testid="api-key-secret" ref={textRef}>{secret}</code>
          <button type="button" className={`btn sw-btn-primary btn-sm ${copy.copied ? 'is-copied' : ''}`} aria-label="Copy the API key" onClick={() => copy.copy(secret, textRef)}>
            {copy.copied ? <IconCheck /> : <IconCopy />} {copy.copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        {copy.manual && <p className="sw-hint small mt-1" role="status">{copy.hint}</p>}
        <p className="sw-hint small mt-2 mb-0">Send it in the Authorization header as <code>Bearer</code> followed by the key. Never put it in a URL.</p>
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn-primary" ref={doneRef} onClick={onDone}>I've stored it</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
